import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { ArrowLeft, ArrowRight, Sparkles, Lock, CheckCircle2 } from "lucide-react";
import { SCORE_BANDS } from "../../lib/quizPreliminaryScore";

const R = 84;
const CIRC = 2 * Math.PI * R;

function ScoreCircle({ score, band }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 1400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(score * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);
  const dash = CIRC * (1 - shown / 100);
  return (
    <div className={`relative mx-auto h-56 w-56 ${band.celebrate ? "quiz-score-pulse" : ""}`} data-testid="quiz-score-circle"
      style={{ "--ring": band.ring }}>
      <svg viewBox="0 0 200 200" className="h-full w-full -rotate-90">
        <circle cx="100" cy="100" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="14" />
        <circle cx="100" cy="100" r={R} fill="none" stroke={band.ring} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={CIRC} strokeDashoffset={dash} style={{ transition: "stroke-dashoffset 80ms linear", filter: `drop-shadow(0 0 10px ${band.ring}80)` }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-6xl font-extrabold tabular-nums text-white" data-testid="quiz-score-number">{shown}</span>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">out of 100</span>
      </div>
    </div>
  );
}

export const BUSINESS_COPY = {
  scoreNote: "Preliminary — based on your answers so far. Your documents set the exact score.",
  fundersHeading: "Based on the information you have given us, you are likely to get funding from the following funders",
  reportTitle: "Sign up to Clinq to find out who can fund your business — at the stage it's at today.",
  subtitle: "Inside Clinq you'll get:",
  bullets: [
    "The funders in our network matched to your business as it stands right now — not where it might be in two years",
    "Your exact Capital Readiness Score based on your documents and full business profile",
    "The likelihood of each funder saying yes — and what to fix to improve your odds",
    "Direct applications to funders within our network, with Clinq making the introduction when your score is strong",
  ],
  cta: "Sign up to see who can fund my business",
  ctaSignedIn: "Show me who can fund my business",
  footer: "Free to start · NDA-protected uploads · Built for African SMEs",
};

export const PROJECT_COPY = {
  scoreNote: "Preliminary — based on your answers so far. Your intake and evidence documents set the exact score.",
  fundersHeading: "Based on the information you have given us, you are likely to get funding from the following funders",
  reportTitle: "Download your Project Readiness Report",
  bullets: [
    "Your exact Project Readiness Score based on your documents and full project profile",
    "A ranked list of every funder in the Clinq network matched to your project",
    "The types of funders most likely to back your project type, stage, and capital structure",
    "An estimate of how much capital your project could potentially raise",
    "The likelihood of each funder saying yes — and what you need to do to improve your odds",
    "Projects with strong scores are actively connected by Clinq to funders within our network — your report is your introduction",
    "A clear action plan showing exactly what to fix if you are not yet ready",
  ],
  cta: "Download Your Project Readiness Report",
  footer: "Free to start · NDA-protected uploads · Built for African projects",
  noun: "project",
};

export function QuizResults({ result, funders, badgeClasses, badgeLabel, onBack, onCta, saveErr, copy = BUSINESS_COPY, shareSlot = null, belowCta = null, children }) {
  const { score, band } = result;
  const bandMessage = band.message.replace(/business/g, copy.noun || "business");
  const fired = useRef(false);
  useEffect(() => {
    if (!band.celebrate || fired.current) return;
    fired.current = true;
    const t = setTimeout(() => {
      const opts = { spread: 80, origin: { y: 0.35 }, colors: ["#4ADE80", "#22C55E", "#F59E0B", "#ffffff"] };
      confetti({ ...opts, particleCount: band.celebrate === 2 ? 220 : 110 });
      if (band.celebrate === 2) setTimeout(() => confetti({ ...opts, particleCount: 160, angle: 60, origin: { x: 0, y: 0.5 } }), 350);
      if (band.celebrate === 2) setTimeout(() => confetti({ ...opts, particleCount: 160, angle: 120, origin: { x: 1, y: 0.5 } }), 350);
    }, 1200);
    return () => clearTimeout(t);
  }, [band]);

  return (
    <section data-testid="quiz-step-5" className="mt-10">
      <style>{`
        @keyframes quizPulse { 0%,100% { filter: drop-shadow(0 0 0 rgba(0,0,0,0)); transform: scale(1);} 50% { filter: drop-shadow(0 0 28px var(--ring)); transform: scale(1.03);} }
        .quiz-score-pulse { animation: quizPulse 2.2s ease-in-out infinite; }
        @keyframes quizRise { from { opacity: 0; transform: translateY(10px);} to { opacity: 1; transform: none;} }
        .quiz-rise { animation: quizRise .6s ease-out both; }
      `}</style>

      <div className="text-center">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">Your preliminary result</p>
        {band.celebrate > 0 && (
          <p className="quiz-rise mx-auto mt-3 max-w-xl text-base font-semibold leading-relaxed text-emerald-200 md:text-lg" data-testid="quiz-score-celebrate-msg">
            <Sparkles className="mr-1.5 inline h-4 w-4 text-emerald-300" />{bandMessage}
          </p>
        )}
        <div className="mt-6"><ScoreCircle score={score} band={band} /></div>
        <p className="mt-4 text-2xl font-extrabold tracking-tight" style={{ color: band.color }} data-testid="quiz-score-band-label">{band.label}</p>
        {band.celebrate === 0 && (
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-white/70" data-testid="quiz-score-encourage-msg">{bandMessage}</p>
        )}
        <p className="mt-3 text-xs text-white/45">{copy.scoreNote}</p>
        {shareSlot}
      </div>

      <div className="mt-10" data-testid="quiz-score-bands">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">Where you sit</p>
        <div className="mt-3 flex h-3 w-full overflow-hidden rounded-full">
          {SCORE_BANDS.map((b) => (
            <div key={b.id} style={{ width: `${b.max - b.min + 1}%`, background: b.color, opacity: b.id === band.id ? 1 : 0.35 }} />
          ))}
        </div>
        <div className="relative mt-1 h-3">
          <div className="absolute -top-[22px] h-6 w-0.5 bg-white" style={{ left: `calc(${score}% - 1px)` }} data-testid="quiz-score-marker" />
        </div>
        <div className="mt-2 grid grid-cols-5 gap-1 text-center">
          {SCORE_BANDS.map((b) => (
            <div key={b.id} className={`rounded-lg px-1 py-2 ${b.id === band.id ? "bg-white/[0.08] ring-1 ring-white/20" : ""}`} data-testid={`quiz-band-${b.id}`}>
              <p className="text-[11px] font-bold leading-tight" style={{ color: b.color }}>{b.label}</p>
              <p className="mt-0.5 text-[10px] text-white/50">{b.min}–{b.max}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <h2 className="text-base font-extrabold tracking-tight text-white md:text-lg">
          {copy.fundersHeading}
        </h2>
        <p className="mt-1 text-xs text-white/50">A preview — your full report ranks every funder in the Clinq network.</p>
        <div className="mt-5 grid gap-3">
          {funders.map((row) => (
            <div key={row.id} data-testid={`quiz-funder-${row.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold tracking-tight text-white">{row.label}</p>
                  <p className="mt-1 text-xs text-white/60">{row.blurb}</p>
                </div>
                <span data-testid={`quiz-funder-${row.id}-badge`}
                  className={"inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider " + badgeClasses(row.likelihood)}>
                  {badgeLabel(row.likelihood)}
                </span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-white/50">{row.rationale}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-3 text-xs text-white/60">
          <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-300" /> More matches, specific funds and contacts unlock with your full report.
        </div>
      </div>

      {children}

      <div className="mt-12 rounded-2xl border border-emerald-400/25 bg-white/[0.04] p-6 sm:p-8" data-testid="quiz-report-cta">
        <h2 className="text-xl font-extrabold tracking-tight text-white sm:text-2xl">{copy.reportTitle}</h2>
        <p className="mt-1 text-sm font-semibold text-emerald-300">{copy.subtitle || "Your full report gives you:"}</p>
        <ul className="mt-4 space-y-2.5 text-sm text-white/80">
          {copy.bullets.map((t) => (
            <li key={t} className="flex gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /><span>{t}</span></li>
          ))}
        </ul>
        {saveErr && <p data-testid="quiz-save-error" className="mt-5 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">{saveErr}</p>}
        <button data-testid="quiz-finish-cta" onClick={onCta}
          className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-semibold tracking-tight text-white shadow-lg shadow-black/40 transition-all hover:bg-[#066667] hover:shadow-emerald-400/30 sm:w-auto">
          {copy.cta}
          <ArrowRight className="h-4 w-4 text-emerald-300 transition-transform group-hover:translate-x-0.5" />
        </button>
        <p className="mt-3 text-[11px] text-white/45">{copy.footer}</p>
      </div>
      {belowCta}

      <button data-testid="quiz-step-5-back" onClick={onBack} className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-white/60 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
    </section>
  );
}
